import React from 'react';
import type { UserInvitation } from '@lnr-v2/types';
import { Button } from '../../atoms/Button';
import { Spinner } from '../../atoms/Spinner';
import { InvitationStatusBadge } from '../../molecules/InvitationStatusBadge';
import styles from './InvitationList.module.css';

interface InvitationListProps {
  invitations: UserInvitation[];
  loading?: boolean;
  onRevoke: (id: string) => void;
  onResend: (email: string) => void;
}

/**
 * Displays a list of pending user invitations
 */
export const InvitationList: React.FC<InvitationListProps> = ({
  invitations,
  loading = false,
  onRevoke,
  onResend,
}) => {
  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner size="lg" />
        <p>Loading invitations...</p>
      </div>
    );
  }

  if (invitations.length === 0) {
    return (
      <div className={styles.empty}>
        <p>No pending invitations</p>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isExpired = (expiryDate: string) => {
    return new Date(expiryDate) < new Date();
  };

  return (
    <div className={styles.container}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Email</th>
            <th>Role</th>
            <th>Invited By</th>
            <th>Sent</th>
            <th>Expires</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {invitations.map((invitation) => (
            <tr key={invitation.id}>
              <td className={styles.email}>{invitation.email}</td>
              <td className={styles.role}>
                <span className={styles.roleBadge}>{invitation.role}</span>
              </td>
              <td>{invitation.invitedByName}</td>
              <td>{formatDate(invitation.invitationSentAt)}</td>
              <td className={isExpired(invitation.invitationExpiry) ? styles.expired : ''}>
                {formatDate(invitation.invitationExpiry)}
              </td>
              <td>
                <InvitationStatusBadge
                  status={invitation.status}
                  expiresAt={invitation.invitationExpiry}
                />
              </td>
              <td className={styles.actions}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onResend(invitation.email)}
                  glass
                >
                  Resend
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => onRevoke(invitation.id)}
                  glass
                >
                  Revoke
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
